import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Globe, Heart, Menu, Phone, ShoppingBag, User, X, Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useSiteMedia } from "@/lib/siteImages";
import { usePublicConfig, telLink, formatPhone } from "@/lib/config";

export default function Header() {
  const { t, lang, setLang, languages } = useLanguage();
  const { count, setOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const media = useSiteMedia();
  const cfg = usePublicConfig();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const onDarkHero = location.pathname === "/";

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/shop", label: t.nav.shop },
    { to: "/lookbook", label: t.nav.lookbook },
    { to: "/about", label: t.nav.about },
    { to: "/science", label: t.nav.science },
    { to: "/blog", label: t.nav.blog },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header data-testid="site-header" className="fixed inset-x-0 top-0 z-50">
      <div className="border-b border-navy/5 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
            {media("logo") ? (
              <img src={media("logo")} alt="Antalia Pharma" data-testid="brand-logo" className="h-9 w-auto object-contain" />
            ) : (
              <span className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/60 font-display text-[13px] font-medium tracking-wide text-gold-dark">
                AP
              </span>
            )}
            <span className="hidden items-baseline gap-2 sm:flex">
              <span className="font-display text-2xl font-medium tracking-tight text-navy">
                Antalia
              </span>
              <span className="label-luxe text-gold">Pharma</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s|&/g, "-")}`}
                className={({ isActive }) =>
                  `nav-link-luxe text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                    isActive ? "text-gold-dark" : "text-navy/60 hover:text-navy"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {cfg?.phone && (
              <a
                href={telLink(cfg.phone)}
                data-testid="header-call-us"
                title={t.contact?.callUs || "Call us"}
                className="hidden items-center gap-1.5 whitespace-nowrap px-2 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-navy/70 transition-colors hover:text-gold-dark xl:flex"
              >
                <Phone size={14} strokeWidth={1.5} />
                <span>{formatPhone(cfg.phone)}</span>
              </a>
            )}
            <div className="relative">
              <button
                data-testid="language-switcher-button"
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-navy/70 transition-colors hover:text-gold-dark"
                aria-label="Change language"
              >
                <Globe size={15} strokeWidth={1.5} />
                <span>{lang.toUpperCase()}</span>
              </button>
              <AnimatePresence>
                {langOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2 }}
                    data-testid="language-menu"
                    className="absolute end-0 top-full mt-2 w-44 border border-navy/10 bg-white py-2 shadow-[0_20px_40px_-15px_rgba(10,17,40,0.15)]"
                  >
                    {languages.map((l) => (
                      <button
                        key={l.code}
                        data-testid={`lang-option-${l.code}`}
                        onClick={() => {
                          setLang(l.code);
                          setLangOpen(false);
                        }}
                        className="flex w-full items-center justify-between px-4 py-2 text-start text-xs font-medium text-navy/70 transition-colors hover:bg-surface hover:text-navy"
                      >
                        <span>{l.label}</span>
                        {lang === l.code && <Check size={13} className="text-gold" />}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/account"
              data-testid="account-link"
              className="relative flex items-center px-3 py-2 text-navy/80 transition-colors hover:text-gold-dark"
              aria-label="Account"
            >
              <User size={18} strokeWidth={1.5} />
            </Link>

            <Link
              to="/wishlist"
              data-testid="wishlist-link"
              className="relative flex items-center px-3 py-2 text-navy/80 transition-colors hover:text-gold-dark"
              aria-label="Wishlist"
            >
              <Heart size={18} strokeWidth={1.5} />
              {wishCount > 0 && (
                <span data-testid="wishlist-count-badge" className="absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center bg-gold px-1 text-[9px] font-bold text-navy">
                  {wishCount}
                </span>
              )}
            </Link>

            <button
              data-testid="cart-open-button"
              onClick={() => setOpen(true)}
              className="relative flex items-center gap-2 px-3 py-2 text-navy/80 transition-colors hover:text-gold-dark"
              aria-label="Open cart"
            >
              <ShoppingBag size={18} strokeWidth={1.5} />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center bg-gold px-1 text-[9px] font-bold text-navy"
                >
                  {count}
                </span>
              )}
            </button>

            <button
              data-testid="mobile-menu-button"
              onClick={() => setMobileOpen(true)}
              className="p-2 text-navy lg:hidden"
              aria-label="Open menu"
            >
              <Menu size={20} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
            className="fixed inset-0 z-50 bg-navy noise-bg"
          >
            <div className="flex items-center justify-between px-6 py-6">
              <span className="font-display text-2xl font-medium text-white">Antalia</span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setMobileOpen(false)}
                className="p-2 text-white/70 hover:text-gold"
                aria-label="Close menu"
              >
                <X size={22} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-col gap-2 px-6 pt-10">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-nav-${i}`}
                    onClick={() => setMobileOpen(false)}
                    className="block border-b border-white/10 py-4 font-display text-3xl font-light text-white transition-colors hover:text-gold"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            {cfg?.phone && (
              <a
                href={telLink(cfg.phone)}
                data-testid="mobile-call-us"
                className="mx-6 mt-8 flex items-center gap-3 border border-white/15 px-4 py-3 text-white/80 transition-colors hover:text-gold"
              >
                <Phone size={16} strokeWidth={1.5} />
                <span className="text-sm font-medium">{t.contact?.callUs || "Call us"} · {formatPhone(cfg.phone)}</span>
              </a>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
